import {
    Container,
    Box,
    Typography,
    Link,
    List,
    ListItemText,
    ListItemIcon,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Drawer,
    ListItemButton,
    Collapse,
    IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useState, useEffect } from 'react';
import { helpContent } from './helpContent';
import Footer from '../informational/Footer';
import { useDispatch } from 'react-redux';
import { showSurvey } from '../feedback/FeedbackSlice';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useNavigate } from 'react-router-dom';

// Debug mode to show screenshot filenames
const DEBUG_MODE = false;

function Help() {
    useScrollToTop();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedSection, setExpandedSection] = useState('Quick Start Guide');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expandedTopics, setExpandedTopics] = useState({ 'Quick Start Guide': true });
    const [stickyHeader, setStickyHeader] = useState(null);
    const [expandedImages, setExpandedImages] = useState({});

    // Handle scroll events for sticky headers
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const headerHeight = 64;

                    // Find the currently visible section
                    const sections = document.querySelectorAll('[id^="section-"]');
                    let currentSection = null;

                    sections.forEach(section => {
                        const rect = section.getBoundingClientRect();
                        const accordion = section.querySelector('.MuiAccordion-root');
                        const isExpanded = accordion && accordion.classList.contains('Mui-expanded');

                        // Check if the section is in the viewport and the accordion is expanded
                        // The section becomes sticky when its top reaches the header position (64px)
                        // Add a small buffer to make the transition smoother
                        if (isExpanded && rect.top <= headerHeight + 10 && rect.bottom > headerHeight) {
                            currentSection = section.id;
                        }
                    });

                    setStickyHeader(currentSection);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        return () => window.removeEventListener('scroll', handleScroll);
    }, [expandedSection]);

    // Always show all content, search is handled by highlighting
    const filteredContent = helpContent;

    const handleSectionToggle = (sectionTitle) => {
        const isCurrentlyExpanded = expandedSection === sectionTitle;

        if (isCurrentlyExpanded) {
            // If currently expanded, close it
            setExpandedSection('');
            setExpandedTopics(prev => ({
                ...prev,
                [sectionTitle]: false
            }));
        } else {
            // If not expanded, open it and close all others
            setExpandedSection(sectionTitle);
            setExpandedTopics(prev => {
                const newState = {};
                // Close all other topics
                helpContent.forEach(section => {
                    newState[section.title] = false;
                });
                // Open the selected topic
                newState[sectionTitle] = true;
                return newState;
            });
        }

        if (isMobile) {
            setDrawerOpen(false);
        }
    };

    const handleTopicToggle = (topicTitle) => {
        const newExpandedState = !expandedTopics[topicTitle];

        if (newExpandedState) {
            // If expanding a topic, close all others and set this as active
            setExpandedSection(topicTitle);
            setExpandedTopics(prev => {
                const newState = {};
                // Close all other topics
                helpContent.forEach(section => {
                    newState[section.title] = false;
                });
                // Open the selected topic
                newState[topicTitle] = true;
                return newState;
            });
        } else {
            // If collapsing a topic, close it and the accordion
            setExpandedSection('');
            setExpandedTopics(prev => ({
                ...prev,
                [topicTitle]: false
            }));
        }
    };

    const handleSubTopicClick = (topicTitle, subTopicTitle) => {
        // Open the topic accordion and close all others
        setExpandedSection(topicTitle);
        // Also expand the topic in the left navigation and close all others
        setExpandedTopics(prev => {
            const newState = {};
            // Close all other topics
            helpContent.forEach(section => {
                newState[section.title] = false;
            });
            // Open the selected topic
            newState[topicTitle] = true;
            return newState;
        });
    };

    const handleFeedbackFormOpen = () => {
        dispatch(showSurvey());
    };

    const highlightSearchTerms = (text, searchQuery) => {
        if (!searchQuery.trim()) return text;

        const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '2px 4px', borderRadius: '3px' }}>
                    {part}
                </mark>
            ) : part
        );
    };

    const renderInstruction = (instruction, searchQuery) => {
        // Support array format: ['text', { type: 'link', text: '...', to: '...' }, 'more text']
        if (Array.isArray(instruction)) {
            return instruction.map((part, index) => {
                if (typeof part === 'string') {
                    return <span key={index}>{highlightSearchTerms(part, searchQuery)}</span>;
                }
                if (part?.type === 'link') {
                    return (
                        <Link
                            key={index}
                            component="button"
                            variant="body2"
                            onClick={() => navigate(part.to)}
                            sx={{
                                color: '#1976d2',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#1565c0'
                                }
                            }}
                        >
                            {highlightSearchTerms(part.text, searchQuery)}
                        </Link>
                    );
                }
                return part;
            });
        }

        // Fallback to string (backward compatible)
        return highlightSearchTerms(instruction, searchQuery);
    };

    const hasSearchMatches = (section, searchQuery) => {
        if (!searchQuery.trim()) return false;

        const query = searchQuery.toLowerCase();
        const instructionMatches = (instruction) => {
            if (Array.isArray(instruction)) {
                return instruction.some(part => {
                    if (typeof part === 'string') return part.toLowerCase().includes(query);
                    if (part?.type === 'link') return part.text.toLowerCase().includes(query);
                    return false;
                });
            }
            return typeof instruction === 'string' && instruction.toLowerCase().includes(query);
        };

        return section.title.toLowerCase().includes(query) ||
            section.subtopics?.some(subtopic =>
                subtopic.title.toLowerCase().includes(query) ||
                instructionMatches(subtopic.instruction)
            );
    };


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
            <Box sx={{ display: 'flex', flex: 1 }}>
                {/* Left Panel - Topics Navigation */}
                <Drawer
                    variant={isMobile ? 'temporary' : 'permanent'}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    sx={{
                        width: { xs: 300, md: 320 },
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: { xs: 300, md: 320 },
                            backgroundColor: '#ffffff',
                            borderRight: '1px solid #e9ecef',
                            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
                            position: 'fixed',
                            height: 'calc(100vh - 64px)',
                            top: '64px',
                            zIndex: 1
                        }
                    }}
                >
                    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
                        {/* Help Topics Header */}
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: '#2c3e50',
                                mb: 3,
                                fontSize: '1.3rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            📚 Help Topics
                        </Typography>

                        {/* Search */}
                        <TextField
                            fullWidth
                            placeholder="Search help topics..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#6c757d' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    backgroundColor: '#f8f9fa',
                                    '&:hover': {
                                        backgroundColor: '#e9ecef'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 0 0 2px #1976d2'
                                    }
                                }
                            }}
                        />

                        {/* Topics List */}
                        <List sx={{ p: 0 }}>
                            {filteredContent.map((section, index) => (
                                <Box key={index} sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleTopicToggle(section.title)}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 0.5,
                                            py: 1,
                                            px: 2,
                                            backgroundColor: expandedTopics[section.title] ? '#e3f2fd' : 'transparent',
                                            borderLeft: hasSearchMatches(section, searchQuery) ? '4px solid #ffeb3b' : '4px solid transparent',
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa',
                                                transform: 'translateX(4px)',
                                                transition: 'all 0.2s ease'
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleTopicToggle(section.title);
                                                }}
                                                sx={{ p: 0.5 }}
                                            >
                                                {expandedTopics[section.title] ? <RemoveIcon sx={{ fontSize: '1rem' }} /> : <AddIcon sx={{ fontSize: '1rem' }} />}
                                            </IconButton>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: expandedTopics[section.title] ? '#1976d2' : '#2c3e50'
                                                    }}
                                                >
                                                    {highlightSearchTerms(section.title, searchQuery)}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>

                                    <Collapse in={expandedTopics[section.title]} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {section.subtopics.map((subtopic, subIndex) => (
                                                <ListItemButton
                                                    key={subIndex}
                                                    onClick={() => handleSubTopicClick(section.title, subtopic.title)}
                                                    sx={{
                                                        pl: 6,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        '&:hover': {
                                                            backgroundColor: '#f8f9fa',
                                                            color: '#1976d2'
                                                        }
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: '0.9rem',
                                                                    color: '#6c757d'
                                                                }}
                                                            >
                                                                • {highlightSearchTerms(subtopic.title, searchQuery)}
                                                            </Typography>
                                                        }
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Collapse>
                                </Box>
                            ))}
                        </List>
                    </Box>
                </Drawer>

                {/* Main Content Area */}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        backgroundColor: '#f8f9fa',
                        backgroundImage: 'url("/landing/hero.jpg")',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundAttachment: 'fixed',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, flex: 1 }}>
                        {/* Mobile Menu Button */}
                        <Box sx={{ mb: 3, display: { xs: 'block', md: 'none' } }}>
                            <Button
                                variant="outlined"
                                startIcon={<MenuIcon />}
                                onClick={() => setDrawerOpen(true)}
                                sx={{
                                    backgroundColor: '#ffffff',
                                    color: '#2c3e50',
                                    borderColor: '#dee2e6',
                                    borderRadius: 3,
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#f8f9fa',
                                        borderColor: '#1976d2',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)'
                                    },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                📚 Browse Help Topics
                            </Button>
                        </Box>

                        {/* Help Content */}
                        {filteredContent.map((section, index) => (
                            <Accordion
                                key={index}
                                expanded={expandedSection === section.title}
                                onChange={() => handleSectionToggle(section.title)}
                                id={`section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                                sx={{
                                    mb: { xs: 2, md: 3 },
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e9ecef',
                                    '&:before': {
                                        display: 'none'
                                    },
                                    '&.Mui-expanded': {
                                        margin: '0 0 24px 0'
                                    }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        backgroundColor: expandedSection === section.title ? '#e3f2fd' : '#ffffff',
                                        borderRadius: '12px 12px 0 0',
                                        px: { xs: 2, md: 3 },
                                        py: { xs: 1.5, md: 2 },
                                        position: stickyHeader === `section-${section.title.toLowerCase().replace(/\s+/g, '-')}` ? 'sticky' : 'relative',
                                        top: stickyHeader === `section-${section.title.toLowerCase().replace(/\s+/g, '-')}` ? '64px' : 'auto',
                                        zIndex: stickyHeader === `section-${section.title.toLowerCase().replace(/\s+/g, '-')}` ? 10 : 'auto',
                                        boxShadow: stickyHeader === `section-${section.title.toLowerCase().replace(/\s+/g, '-')}` ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                                        backdropFilter: stickyHeader === `section-${section.title.toLowerCase().replace(/\s+/g, '-')}` ? 'blur(8px)' : 'none',
                                        borderLeft: hasSearchMatches(section, searchQuery) ? '4px solid #ffeb3b' : '4px solid transparent',
                                        '&:hover': {
                                            backgroundColor: expandedSection === section.title ? '#e3f2fd' : '#f8f9fa'
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#2c3e50',
                                            fontSize: '1.3rem'
                                        }}
                                    >
                                        {highlightSearchTerms(section.title, searchQuery)}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                                        {section.subtopics.map((subtopic, subIndex) => (
                                            <Box
                                                key={subIndex}
                                                id={`subtopic-${subtopic.title.toLowerCase().replace(/\s+/g, '-')}`}
                                                sx={{
                                                    mb: { xs: 3, md: 4 },
                                                    p: { xs: 2, md: 3 },
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: 2,
                                                    border: '1px solid #e9ecef',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#2c3e50',
                                                        mb: 2,
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {highlightSearchTerms(subtopic.title, searchQuery)}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: '#6c757d',
                                                        lineHeight: 1.7,
                                                        mb: subtopic.screenshot ? 3 : 0
                                                    }}
                                                >
                                                    {renderInstruction(subtopic.instruction, searchQuery)}
                                                </Typography>

                                                {/* Special handling for Still Need Help section */}
                                                {section.title === 'Still Need Help' && (
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                                                        {subtopic.title === 'Contact Support' && (
                                                            <Button
                                                                variant="contained"
                                                                component={Link}
                                                                href={`mailto:contact@cuemeup.com`}
                                                                sx={{
                                                                    backgroundColor: '#1976d2',
                                                                    '&:hover': {
                                                                        backgroundColor: '#1565c0'
                                                                    }
                                                                }}
                                                            >
                                                                Email Support
                                                            </Button>
                                                        )}
                                                        {subtopic.title === 'Feedback Form' && (
                                                            <Button
                                                                variant="outlined"
                                                                onClick={handleFeedbackFormOpen}
                                                                sx={{
                                                                    borderColor: '#1976d2',
                                                                    color: '#1976d2',
                                                                    '&:hover': {
                                                                        borderColor: '#1565c0',
                                                                        backgroundColor: '#e3f2fd'
                                                                    }
                                                                }}
                                                            >
                                                                Open Feedback Form
                                                            </Button>
                                                        )}
                                                    </Box>
                                                )}

                                                {subtopic.screenshot && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Accordion
                                                            expanded={expandedImages[`${section.title}-${subtopic.title}`] || false}
                                                            onChange={() => {
                                                                const key = `${section.title}-${subtopic.title}`;
                                                                setExpandedImages(prev => ({
                                                                    ...prev,
                                                                    [key]: !prev[key]
                                                                }));
                                                            }}
                                                            sx={{
                                                                boxShadow: 'none',
                                                                border: '1px solid #e9ecef',
                                                                borderRadius: 2,
                                                                '&:before': {
                                                                    display: 'none'
                                                                },
                                                                '&.Mui-expanded': {
                                                                    margin: 0
                                                                }
                                                            }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={<ExpandMoreIcon />}
                                                                sx={{
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: 2,
                                                                    px: 2,
                                                                    py: 1,
                                                                    '&:hover': {
                                                                        backgroundColor: '#e9ecef'
                                                                    }
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: '#2c3e50'
                                                                    }}
                                                                >
                                                                    📸 Show Screenshots
                                                                </Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails sx={{ p: 2 }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: 2,
                                                                        justifyContent: 'flex-start',
                                                                        alignItems: 'flex-start'
                                                                    }}
                                                                >
                                                                    {(Array.isArray(subtopic.screenshot) ? subtopic.screenshot : [subtopic.screenshot]).map((image, imageIndex) => (
                                                                        <Box
                                                                            key={imageIndex}
                                                                            sx={{
                                                                                width: 'calc(33.333% - 8px)',
                                                                                minWidth: '200px',
                                                                                borderRadius: 2,
                                                                                overflow: 'hidden',
                                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                                                border: '1px solid #e9ecef'
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={`/help-screenshots/${image}`}
                                                                                alt={`Screenshot ${imageIndex + 1} showing ${subtopic.title.toLowerCase()}`}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: 'auto',
                                                                                    display: 'block'
                                                                                }}
                                                                                onError={(e) => {
                                                                                    // Fallback to placeholder if image doesn't exist
                                                                                    e.target.style.display = 'none';
                                                                                    e.target.nextSibling.style.display = 'flex';
                                                                                }}
                                                                            />
                                                                            {DEBUG_MODE && (
                                                                                <Box
                                                                                    sx={{
                                                                                        backgroundColor: '#f8f9fa',
                                                                                        p: 1,
                                                                                        borderTop: '1px solid #e9ecef'
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: '#6c757d',
                                                                                            fontSize: '0.7rem',
                                                                                            fontFamily: "'JetBrains Mono', 'Fira Code', monospace"
                                                                                        }}
                                                                                    >
                                                                                        {image}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Box
                                                                                sx={{
                                                                                    backgroundColor: '#f8f9fa',
                                                                                    p: 2,
                                                                                    textAlign: 'center',
                                                                                    border: '2px dashed #dee2e6',
                                                                                    minHeight: '120px',
                                                                                    display: 'none',
                                                                                    flexDirection: 'column',
                                                                                    justifyContent: 'center',
                                                                                    alignItems: 'center'
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        color: '#6c757d',
                                                                                        fontStyle: 'italic',
                                                                                        fontSize: '0.8rem'
                                                                                    }}
                                                                                >
                                                                                    📸 {image.replace('.png', '').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Container>
                </Box>
            </Box>

            <Footer />
        </Box>
    );
}

export default Help; 
